/**
 * HospitalFinderPage
 *
 * Displayed at bare /login and /register routes (without :hospitalSlug).
 * Prompts the user to enter their hospital code and navigates to the
 * slug-specific login or register page.
 *
 * Design: matches the clinical design system used by LoginPage / RegisterPage.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface HospitalFinderPageProps {
  /** Where to navigate after slug entry: 'login' or 'register' */
  mode: 'login' | 'register';
}

export function HospitalFinderPage({ mode }: HospitalFinderPageProps) {
  const navigate = useNavigate();
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isLogin = mode === 'login';
  const heading = isLogin ? 'Sign in' : 'Create an account';
  const subheading = isLogin
    ? 'Enter your hospital code to continue'
    : 'Enter your hospital code to register';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = slug.trim().toLowerCase().replace(/\s+/g, '-');
    if (!trimmed) {
      setError('Please enter a hospital code.');
      return;
    }
    setError(null);
    navigate(`/${mode}/${trimmed}`);
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-clinical-charcoal flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-7 h-7 text-white"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z"
              />
            </svg>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-clinical-charcoal leading-tight">
            {heading}
          </h1>
          <p className="text-sm font-body text-clinical-text-muted mt-1">
            {subheading}
          </p>
        </div>

        {/* Form card */}
        <div className="clinical-card p-6">
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            <div>
              <label htmlFor="hospital-slug" className="form-label">
                Hospital code
              </label>
              <input
                id="hospital-slug"
                type="text"
                autoComplete="organization"
                autoFocus
                required
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  if (error) setError(null);
                }}
                className="form-input"
                placeholder="e.g. king-faisal"
              />
              <p className="text-xs font-body text-clinical-text-muted mt-1.5">
                Your hospital administrator provided this code when setting up your account.
              </p>
            </div>

            {error && (
              <div
                role="alert"
                className="p-3 rounded-clinical bg-red-50 border border-red-200 text-sm font-body text-red-700"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={slug.trim().length === 0}
              className="btn-primary w-full"
            >
              Continue
            </button>
          </form>
        </div>

        {/* Toggle link */}
        <p className="text-center text-sm font-body text-clinical-text-muted mt-6">
          {isLogin ? (
            <>
              Need an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-clinical-teal hover:text-clinical-teal-dark underline-offset-2 hover:underline transition-colors"
              >
                Register here
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-clinical-teal hover:text-clinical-teal-dark underline-offset-2 hover:underline transition-colors"
              >
                Sign in
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
